import { UserRole } from '@/permissions/types';

/**
 * Company interface
 */
export interface Company {
  id: number;
  name: string;
}

/**
 * UserGroup interface
 */
export interface UserGroup {
  id: number;
  code: string;
  name: string;
}

/**
 * Current User Model
 * Represents the authenticated user with their roles and permissions
 * Based on actual API response from /api/users/me
 */
export interface CurrentUser {
  id: number;
  name: string;
  email: string;
  roleCodes: UserRole[];
  companies: Company[];
  userGroups: UserGroup[];
  inviteStatus: string;
  whatsAppNumber?: string;
  isNotifyMonday?: boolean;
  isNotifyTuesday?: boolean;
  isNotifyWednesday?: boolean;
  isNotifyThursday?: boolean;
  isNotifyFriday?: boolean;
}

/**
 * API response structure for user data from /api/users/me
 */
export interface CurrentUserResponse {
  id: number;
  name: string;
  email: string;
  roleCodes: string[];
  companies: Company[];
  userGroups: UserGroup[];
  inviteStatus: string;
  whatsAppNumber?: string;
  isNotifyMonday?: boolean;
  isNotifyTuesday?: boolean;
  isNotifyWednesday?: boolean;
  isNotifyThursday?: boolean;
  isNotifyFriday?: boolean;
}

/**
 * Deserialize API response to CurrentUser model
 * Handles type conversion and validation of role codes
 */
export function deserializeCurrentUser(response: CurrentUserResponse): CurrentUser {
  const filteredRoles = (response.roleCodes || []).filter((role): role is UserRole => {
    const isValid = Object.values(UserRole).includes(role as UserRole);
    if (!isValid) {
      console.warn('[CurrentUser] Filtering out invalid role:', role);
    }
    return isValid;
  });

  return {
    id: response.id,
    name: response.name,
    email: response.email,
    roleCodes: filteredRoles,
    companies: response.companies || [],
    userGroups: response.userGroups || [],
    inviteStatus: response.inviteStatus,
    whatsAppNumber: response.whatsAppNumber,
    isNotifyMonday: response.isNotifyMonday,
    isNotifyTuesday: response.isNotifyTuesday,
    isNotifyWednesday: response.isNotifyWednesday,
    isNotifyThursday: response.isNotifyThursday,
    isNotifyFriday: response.isNotifyFriday,
  };
}

/**
 * Get user display name
 */
export function getUserDisplayName(user: CurrentUser): string {
  return user.name || user.email;
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(user: CurrentUser): string {
  const nameParts = user.name.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }
  if (user.name) return user.name.substring(0, 2).toUpperCase();
  if (user.email) return user.email.substring(0, 2).toUpperCase();
  return 'U';
}
